'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

/**
 * Floating "back to top" button — appears once the hero is scrolled past.
 * `raised` lifts it on phones above a bottom bar (product page buy bar).
 */
export default function BackToTop({ raised = false }: { raised?: boolean }) {
  const t = useTranslations();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label={t('backToTop')}
      tabIndex={visible ? 0 : -1}
      className={`group fixed z-40 right-5 sm:right-8 ${raised ? 'bottom-24 lg:bottom-8' : 'bottom-5 sm:bottom-8'} w-12 h-12 sm:w-[max(3.5rem,50px)] sm:h-[max(3.5rem,50px)] rounded-full flex items-center justify-center border border-[var(--copper)] bg-[rgba(11,9,7,0.78)] backdrop-blur-md text-[var(--copper)] shadow-[0_8px_30px_rgba(0,0,0,0.45)] transition-[opacity,transform,background-color,color] duration-500 hover:bg-[var(--copper)] hover:text-[var(--ink)] ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
    >
      <svg
        width="16"
        height="18"
        viewBox="0 0 16 18"
        fill="none"
        aria-hidden="true"
        className="transition-transform duration-500 group-hover:-translate-y-0.5"
      >
        <path d="M8 17V1.5M1.5 8L8 1.5 14.5 8" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </button>
  );
}
